import { Inline, InlineProperties, Value, Editor } from "slate";

import { LinkAttributes } from ".";
import { INLINES } from "../../html";

const { type } = INLINES.LINK;

/**
 * Checks if the current selection contains any links
 */
export const hasLinks = (value: Value): boolean => value.inlines.some(inline => inline.type === type);

/**
 * Get the link in the current selection
 */
export const getLink = (value: Value): Inline => value.inlines.filter(inline => inline.type === type).first();

export const getFocusedLink = (value: Value): Inline | null => {
  return value.focusInline && value.focusInline.type === type
    ? value.focusInline
    : null;
};

/**
 * Returns an InlineProperties object, used to wrap selections in inline blocks
 */
export const createLink = (data: LinkAttributes): InlineProperties => ({ type, data: { ...data, target: "_blank" } });

/**
 * Checks if the current selection has more than 1 block
 */
export const hasMultiBlocks = (value: Value): boolean => value.blocks.size > 1;

/**
 * Removes link from current selection
 */
export const removeLink = (editor: Editor): void => {
  editor
    .unwrapInline(type)
    .focus();
};

/**
 * Select the word at the current position of the cursor.
 */
export const selectCurrentWord = (editor: Editor): Editor => {
  // Bail if there's already a selection
  if (editor.value.selection.isExpanded) {
    return editor;
  }

  // Select until non-word characters are found
  const isWordCharacter = (char: string) => /[\w\-]/.test(char);

  const allText = editor.value.document.text;
  let iterations = 0;

  /**
   * ==> While loop conditions
   * (1) reached start of document
   * If we reach the beginning of the document, we don't want to try and move the
   * start of the selection back any further, simples.
   *
   * (2) reached a non-word character
   * We know initially that the editor.value.fragment is zero length because if
   * it "isExpanded" ie has length > 0, we return above. The first character of
   * an empty string is undefined (""[0] === undefined). /[\w-]/.test(undefined)
   * casts its arg to "undefined", tests against that string and returns true.
   * Therefore, check for empty string and then test first character.
   *
   * (3) reached max iterations = num characters
   * #2200 documents a crash where the following while loop never terminated.
   * The sensible maximum number of iterations permissible by this loop is
   * the number of characters in the editor - even if the rest of the logic is
   * flawed, let's prevent a browser crash with a finite ceiling on iterations.
   */

  while (
    !editor.value.selection.start.isAtStartOfNode(editor.value.document) &&
    (editor.value.fragment.text === "" || (isWordCharacter(editor.value.fragment.text[0]))) &&
    iterations <= allText.length
  ) {
    editor.moveStartBackward(1);
    iterations++;
  }

  // if we've got a link in our selection, remove the selection and bail
  // otherwise we'll end up nesting an <a> inside an <a> and badness ensues
  if (getLink(editor.value)) {
    return editor.deselect();
  }

  // if we caught an extra space preceding a selected word
  // remove it from the selection now
  if (!isWordCharacter(editor.value.fragment.text[0])) {
    editor.moveStartForward();
  }

  // Expand selection forwards
  while (isWordCharacter(editor.value.fragment.text[editor.value.fragment.text.length - 1])
    && !editor.value.selection.end.isAtEndOfNode(editor.value.document)) {
    editor.moveEndForward(1);
  }
  if (!isWordCharacter(editor.value.fragment.text[editor.value.fragment.text.length - 1])) {
    editor.moveEndBackward();
  }

  return editor;
};

/**
 * Creates and inserts a link into current position in editor.
 * If text is selected, it will be linkified. If it isn't,
 * the current word will be linkified.
 * @param {Editor} editor
 * @returns {Editor}
 */
export const insertLink = (editor: Editor, attrs?: LinkAttributes): Editor => {
  // If there's already a link here, remove the link
  if (hasLinks(editor.value)) {
    editor.unwrapInline(type);
  }

  // If there isn't a selection, expand to select the current word
  if (editor.value.selection.isCollapsed) {
    selectCurrentWord(editor);

    // If no word could be selected, bail. (e.g. if we were in a sea of whitespace)
    if (editor.value.selection.isCollapsed) {
      return editor;
    }
  }

  // Wrap the current text/node(s) with a link.
  editor.wrapInline(createLink(attrs)).moveToStartOfInline();

  return editor;
};

/**
 * Update properties of the link in the current selection
 */
export const updateLink = (editor: Editor, data: { href: string, target: string }): Editor => {
  const link = getFocusedLink(editor.value);

  if (link != null) {
    editor.setInlines({ type, data });
  }

  return editor;
};
